import { Select, Stack } from '@mantine/core';
import { useEffect, useMemo } from 'react';
import type { Device } from '@types';
import { DEV_TOOLS_COMBOBOX_PROPS } from './comboboxProps';

type DeviceFieldsProps = {
  devices: Device[];
  deviceId: string;
  sensorId: string;
  onDeviceIdChange: (value: string) => void;
  onSensorIdChange: (value: string) => void;
  requireSensor?: boolean;
};

export function DeviceFields({
  devices,
  deviceId,
  sensorId,
  onDeviceIdChange,
  onSensorIdChange,
  requireSensor = false,
}: DeviceFieldsProps) {
  const vrOptions = useMemo(
    () =>
      devices
        .filter(d => d.type === 'vr-headset')
        .map(d => ({
          value: String(d.id),
          label: d.name || d.code || `VR ${d.id}`,
        })),
    [devices]
  );

  const sensorOptions = useMemo(
    () =>
      devices
        .filter(d => d.type === 'smartwatch')
        .map(d => ({
          value: String(d.id),
          label: d.name || d.code || `Sensore ${d.id}`,
        })),
    [devices]
  );

  useEffect(() => {
    if (!deviceId || sensorOptions.length === 0) return;
    const paired = devices.find(
      d =>
        d.type === 'smartwatch' &&
        (d.connected_to === Number(deviceId) ||
          String(d.connected_to) === deviceId)
    );
    if (paired && String(paired.id) !== sensorId) {
      onSensorIdChange(String(paired.id));
    }
  }, [deviceId, devices, onSensorIdChange, sensorId, sensorOptions.length]);

  return (
    <Stack gap='xs'>
      <Select
        label='Visore'
        placeholder='Seleziona visore'
        data={vrOptions}
        value={deviceId || null}
        onChange={v => onDeviceIdChange(v ?? '')}
        searchable
        required
        comboboxProps={DEV_TOOLS_COMBOBOX_PROPS}
      />
      <Select
        label='Sensore'
        placeholder={requireSensor ? 'Seleziona sensore' : 'Opzionale'}
        data={sensorOptions}
        value={sensorId || null}
        onChange={v => onSensorIdChange(v ?? '')}
        searchable
        clearable={!requireSensor}
        required={requireSensor}
        comboboxProps={DEV_TOOLS_COMBOBOX_PROPS}
      />
    </Stack>
  );
}
